<template>
  <div class="flex flex-col">
    <PageTitleHeader title="NFT Mints" />
    <DetailsBox class="mb-2 mt-4">
      Showing all <span class="text-primary">{{ data?.formattedTotalCount }}</span> results
      <template #pagination>
        <BcPagination
          v-model="pagination"
          :total-count="data?.viewCount"
          :row-per-page-list="rowPerPageList"
          hide-rows-per-page
        />
      </template>
    </DetailsBox>
    <Card>
      <CustomSuspense
        :is-loading="isLoading"
        :is-empty="isError || isEmpty(data?.mintList)"
      >
        <template #loading>
          <LoadingTable
            :row-cnt="pagination.rowPerPage"
            :cols="column"
          />
        </template>
        <template #empty>
          <NoDataPreview />
        </template>
        <template #default>
          <BcTable
            :rows="data?.mintList ?? []"
            :cols="column"
            @change-table-data="toggle"
          >
            <template #transactionHash="{row}: {row: NftMintModel}">
              <TransactionCopyLink
                :hash="row.transactionHash"
                class="link"
              />
            </template>
            <template #minterAddressInfo="{row}: {row: NftMintModel}">
              <AddressCopyLink
                class="link"
                :address="row.minterAddressInfo.address"
                :name="row.minterAddressInfo.name"
                :type="row.minterAddressInfo.addressType"
              />
            </template>
            <template #tokenInfo="{row}: {row: NftMintModel}">
              <div class="flex w-full items-center gap-x-2">
                <BcLink
                  :content="row.tokenInfo.name ? `${row.tokenInfo.name}${row.tokenInfo.symbol && ` (${row.tokenInfo.symbol})`}` : row.tokenInfo.address "
                  :to="routes.getNftDetailPageWithTab(row.nftType, row.tokenInfo.address)"
                  class="w-full min-w-0 grow"
                >
                  <template #prefix>
                    <img
                      :src="row.tokenInfo.logo"
                      :alt="`${row.tokenInfo.name} symbol image`"
                      class="h-5 w-5 rounded-full"
                    >
                  </template>
                </BcLink>
              </div>
            </template>
            <template #item="{ row }: { row: NftMintModel }">
              <div class="flex w-full items-center gap-x-2">
                <BcLink
                  :content="row.tokenName || '-'"
                  :to="routes.getNftItemDetailPageWithTab(row.nftType, row.tokenInfo.address, row.tokenId)"
                >
                  <template #prefix>
                    <img
                      :src="row.tokenImage || (IMAGE_BASE_URL + '/token/default.png')"
                      :alt="`${row.tokenName} image`"
                      class="h-5 w-5 rounded-full"
                    >
                  </template>
                </BcLink>
                <IconButton
                  icon="copy"
                  @click="copy(row.tokenId)"
                />
              </div>
            </template>
          </BcTable>
        </template>
      </CustomSuspense>
      <BcPagination
        v-model="pagination"
        :total-count="data?.viewCount"
        :row-per-page-list="rowPerPageList"
      />
    </Card>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { nftService } from '~/api/service/nft.service'
import DetailsBox from '~/components/box/DetailsBox.vue'
import IconButton from '~/components/button/IconButton.vue'
import PageTitleHeader from '~/components/header/PageTitleHeader.vue'
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import TransactionCopyLink from '~/components/link/TransactionCopyLink.vue'
import NoDataPreview from '~/components/preview/NoDataPreview.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import { useCopy } from '~/composables/useCopy'
import { useTableColumn } from '~/composables/useTable'
import { IMAGE_BASE_URL } from '~/constants/image.constant'
import { NFT_MINTS_COLUMN } from '~/constants/token.constants'
import { NftMintModel } from '~/types/model/nft.model'
import BcLink from '~/ui/Link/BcLink.vue'
import BcPagination from '~/ui/Pagination/BcPagination.vue'
import BcTable from '~/ui/Table/BcTable.vue'

const route = useRoute()
const { copy } = useCopy()
const { pagination, rowPerPageList } = usePagination({
  page: 1,
  rowPerPage: 25
})

const { data, isLoading, isError } = nftService.useGetNftMintList(pagination)

useSeoMeta(getMeta('NFT Mints', 'View the latest NFT mints'))

const path = route.fullPath.split('/').filter(elem => elem)
const localStorageKey = path.length > 1 ? `${path[0]}-${path[path.length - 1]}` : path[0]

const hideAge = ref()

if (process.client) {
  hideAge.value = JSON.parse(window.localStorage.getItem(localStorageKey) || 'false')
}

const exceptList = computed(() => {
  return hideAge.value ? [ 'age' ] : [ 'unixTimeStamp' ]
})

const toggle = () => {
  if (process.client) {
    hideAge.value = !hideAge.value
    window.localStorage.setItem(localStorageKey, JSON.stringify(hideAge.value))
  }
}
const { column } = useTableColumn(NFT_MINTS_COLUMN, exceptList)
</script>

<style scoped lang="scss">
.link {
  max-width: 200px;

  @include over_xl {
    max-width: 270px;
  }
}
</style>